import { useI18n } from '@muse/i18n'
import { useHydratedValue, useNavigate } from '@muse-web/router'
import { toast } from '@ds/components'

import { type AnalyticsPlacementTypes, sendEvent, setAnalyticsTrigger } from '@core/analytics'
import { getSignInUrl } from '@core/routes'
import { useActions } from '@core/store'
import { authIntentStore } from '@features/auth'

import { useCurrentUser } from '@features/session'
import { useFollow } from './useFollow'
import { useFollowCheck } from './useFollowCheck'
import { useUnfollow } from './useUnfollow'

type TUseFollowActionParams = {
    username: string
    creatorUserId: string
    placement: AnalyticsPlacementTypes
    onUnfollow?: (dateUnfollowed: number) => void
}

type TUseFollowActionResult = {
    /** Your own profile — callers render nothing. */
    isOwnProfile: boolean
    isFollowing: boolean
    loading: boolean
    label: string
    toggle: () => void
}

/** Follow state + toggle shared by the follow button and the audio-card menu item. */
export const useFollowAction = ({
    username,
    creatorUserId,
    placement,
    onUnfollow,
}: TUseFollowActionParams): TUseFollowActionResult => {
    const { t } = useI18n()
    const navigate = useNavigate()
    const { setIntent } = useActions({ setIntent: authIntentStore.actions.setIntent })
    const { id: myId, username: myUsername } = useCurrentUser()
    const isAuthenticated = myId !== 0
    const isOwnProfile = isAuthenticated && username === myUsername

    const followCheck = useFollowCheck(username, isAuthenticated && !isOwnProfile)
    const follow = useFollow()
    const unfollow = useUnfollow()

    const isFollowing = useHydratedValue(followCheck.data ?? false, false)
    const loading = followCheck.isLoading || follow.isPending || unfollow.isPending

    const creatorParams: {
        params__key: ['creator_user_id']
        params__str_value: [string]
    } = {
        params__key: ['creator_user_id'],
        params__str_value: [creatorUserId],
    }

    const toggle = () => {
        // `isFollowing` reads false until the check resolves, so callers without a disabled state
        // (the card menu item) would re-follow.
        if (loading) {
            return
        }

        sendEvent({ name: 'follow_button_click', payload: { placement, ...creatorParams } })

        if (!isAuthenticated) {
            setAnalyticsTrigger('follow_click')
            setIntent({ intent: { kind: 'follow', username }, at: Date.now() })
            navigate(getSignInUrl(window.location.href))

            return
        }

        if (isFollowing) {
            sendEvent({ name: 'unfollow_click', payload: { placement, ...creatorParams } })
            unfollow.mutate(
                { username, myUsername },
                {
                    onSuccess: result => onUnfollow?.(result.date_unfollowed),
                    onError: () => toast.error(t('i18n.followErrorUnfollow')),
                },
            )

            return
        }

        sendEvent({ name: 'follow_click', payload: { placement, ...creatorParams } })
        follow.mutate(
            { username, myUsername },
            { onError: () => toast.error(t('i18n.followErrorFollow')) },
        )
    }

    return {
        isOwnProfile,
        isFollowing,
        loading,
        label: t(isFollowing ? 'i18n.followButtonFollowing' : 'i18n.followButtonFollow'),
        toggle,
    }
}
